/**
 * Enhanced Currency Formatter with dynamic currency support
 */

// Default currency settings (fallback)
const DEFAULT_CURRENCY_SETTINGS = {
  currencyCode: 'INR',
  currencySymbol: '₹',
  decimalPlaces: 2,
  thousandSeparator: ',',
  decimalSeparator: '.',
  languageCode: 'en-IN'
};

// Try to load from session storage on initialization
const loadSettingsFromStorage = () => {
  try {
    const stored = sessionStorage.getItem('currency_settings');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Could not load currency settings from storage:', error);
  }
  return { ...DEFAULT_CURRENCY_SETTINGS };
};

// Global currency settings (loaded from company settings or session storage)
let globalCurrencySettings = loadSettingsFromStorage();

/**
 * Set global currency settings from company settings
 * @param {Object} settings - Currency settings from backend
 */
export const setCurrencySettings = (settings) => {
  console.log('💱 [CURRENCY FORMATTER] setCurrencySettings called with:', settings);
  console.log('💱 [CURRENCY FORMATTER] Previous settings:', globalCurrencySettings);
  
  globalCurrencySettings = {
    currencyCode: settings.currencyCode || DEFAULT_CURRENCY_SETTINGS.currencyCode,
    currencySymbol: settings.currencySymbol || DEFAULT_CURRENCY_SETTINGS.currencySymbol,
    decimalPlaces: settings.decimalPlaces || DEFAULT_CURRENCY_SETTINGS.decimalPlaces,
    thousandSeparator: settings.thousandSeparator || DEFAULT_CURRENCY_SETTINGS.thousandSeparator,
    decimalSeparator: settings.decimalSeparator || DEFAULT_CURRENCY_SETTINGS.decimalSeparator,
    languageCode: settings.languageCode || DEFAULT_CURRENCY_SETTINGS.languageCode
  };
  
  console.log('💱 [CURRENCY FORMATTER] New settings applied:', globalCurrencySettings);
  
  // Also save to session storage
  try {
    sessionStorage.setItem('currency_settings', JSON.stringify(globalCurrencySettings));
    console.log('💱 [CURRENCY FORMATTER] Settings saved to session storage');
  } catch (error) {
    console.warn('💱 [CURRENCY FORMATTER] Could not save to session storage:', error);
  }
};

/**
 * Get current currency settings
 * @returns {Object} Current currency settings
 */
export const getCurrencySettings = () => {
  return { ...globalCurrencySettings };
};

/**
 * Format currency amount using current settings
 * @param {number|string} amount - Amount to format
 * @param {Object} options - Override options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, options = {}) => {
  if (amount == null || amount === '') return '-';
  
  const settings = { ...globalCurrencySettings, ...options };
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) return '-';
  
  // Debug logging (only log occasionally to avoid spam)
  if (Math.random() < 0.05) { // Log ~5% of calls
    console.log('💵 [FORMAT] formatCurrency called:', {
      amount,
      settings,
      currentGlobalSettings: globalCurrencySettings
    });
  }
  
  try {
    // Use Intl.NumberFormat for proper localization
    const formatter = new Intl.NumberFormat(settings.languageCode, {
      style: 'currency',
      currency: settings.currencyCode,
      minimumFractionDigits: settings.decimalPlaces,
      maximumFractionDigits: settings.decimalPlaces
    });
    
    const result = formatter.format(numAmount);
    
    // Debug first call
    if (!window.__currencyFormatterFirstCall) {
      window.__currencyFormatterFirstCall = true;
      console.log('💵 [FORMAT] First formatCurrency result:', {
        input: amount,
        output: result,
        settings
      });
    }
    
    return result;
  } catch (error) {
    // Fallback to manual formatting if Intl fails
    console.warn('⚠️ [FORMAT] Intl.NumberFormat failed, using fallback formatting:', error);
    return formatCurrencyFallback(numAmount, settings);
  }
};

/**
 * Format currency without symbol
 * @param {number|string} amount - Amount to format
 * @param {Object} options - Override options
 * @returns {string} Formatted number string
 */
export const formatCurrencyWithoutSymbol = (amount, options = {}) => {
  if (amount == null || amount === '') return '-';
  
  const settings = { ...globalCurrencySettings, ...options };
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) return '-';
  
  try {
    const formatter = new Intl.NumberFormat(settings.languageCode, {
      minimumFractionDigits: settings.decimalPlaces,
      maximumFractionDigits: settings.decimalPlaces
    });
    
    return formatter.format(numAmount);
  } catch (error) {
    return formatNumberFallback(numAmount, settings);
  }
};

/**
 * Fallback currency formatting when Intl is not available
 * @param {number} amount - Amount to format
 * @param {Object} settings - Currency settings
 * @returns {string} Formatted currency string
 */
const formatCurrencyFallback = (amount, settings) => {
  const formattedNumber = formatNumberFallback(amount, settings);
  return `${settings.currencySymbol}${formattedNumber}`;
};

/**
 * Fallback number formatting
 * @param {number} amount - Amount to format
 * @param {Object} settings - Currency settings
 * @returns {string} Formatted number string
 */
const formatNumberFallback = (amount, settings) => {
  const fixed = amount.toFixed(settings.decimalPlaces);
  const parts = fixed.split('.');
  
  // Add thousand separators
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, settings.thousandSeparator);
  
  // Join with decimal separator
  return parts.join(settings.decimalSeparator);
};

/**
 * Parse currency string to number
 * @param {string} currencyString - Currency string to parse
 * @returns {number} Parsed number
 */
export const parseCurrency = (currencyString) => {
  if (!currencyString || typeof currencyString !== 'string') return 0;
  
  const settings = globalCurrencySettings;
  
  // Remove currency symbol and spaces
  let cleaned = currencyString.replace(settings.currencySymbol, '').trim();
  
  // Replace thousand separators
  cleaned = cleaned.replace(new RegExp(`\\${settings.thousandSeparator}`, 'g'), '');
  
  // Replace decimal separator with dot
  if (settings.decimalSeparator !== '.') {
    cleaned = cleaned.replace(settings.decimalSeparator, '.');
  }
  
  return parseFloat(cleaned) || 0;
};

/**
 * Get currency symbol
 * @returns {string} Current currency symbol
 */
export const getCurrencySymbol = () => {
  return globalCurrencySettings.currencySymbol;
};

/**
 * Get currency code
 * @returns {string} Current currency code
 */
export const getCurrencyCode = () => {
  return globalCurrencySettings.currencyCode;
};

/**
 * Format percentage
 * @param {number|string} value - Value to format as percentage
 * @param {number} decimalPlaces - Number of decimal places
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimalPlaces = 1) => {
  if (value == null || value === '') return '-';
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '-';
  
  try {
    const formatter = new Intl.NumberFormat(globalCurrencySettings.languageCode, {
      style: 'percent',
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    });
    
    return formatter.format(numValue / 100);
  } catch (error) {
    return `${numValue.toFixed(decimalPlaces)}%`;
  }
};

/**
 * Popular currency configurations
 */
export const CURRENCY_CONFIGS = {
  USD: {
    currencyCode: 'USD',
    currencySymbol: '$',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-US'
  },
  EUR: {
    currencyCode: 'EUR',
    currencySymbol: '€',
    decimalPlaces: 2,
    thousandSeparator: '.',
    decimalSeparator: ',',
    languageCode: 'de-DE'
  },
  GBP: {
    currencyCode: 'GBP',
    currencySymbol: '£',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-GB'
  },
  SGD: {
    currencyCode: 'SGD',
    currencySymbol: 'S$',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-SG'
  },
  JPY: {
    currencyCode: 'JPY',
    currencySymbol: '¥',
    decimalPlaces: 0,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'ja-JP'
  },
  INR: {
    currencyCode: 'INR',
    currencySymbol: '₹',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-IN'
  },
  CAD: {
    currencyCode: 'CAD',
    currencySymbol: 'C$',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-CA'
  },
  AUD: {
    currencyCode: 'AUD',
    currencySymbol: 'A$',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    languageCode: 'en-AU'
  }
};

// Replace the old formatCurrency function
export { formatCurrency as default };
